import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ChartColumnIncreasing } from 'lucide-react';
import { type ComponentProps, useState } from 'react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { BillForm } from '../../components/bills/BillForm';
import { BillsList } from '../../components/bills/BillsList';
import { EmptyState } from '../../components/common/EmptyState';
import { CardSkeleton } from '../../components/common/Skeleton';
import { trpc } from '../../lib/trpc';
import { usePageTitle } from '../../lib/usePageTitle';

type EditableBill = ComponentProps<typeof BillForm>['bill'];
type EditableBillInput = NonNullable<EditableBill>;

const searchSchema = z.object({
  bill: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/bills/')({
  validateSearch: searchSchema,
  component: BillsPage,
});

function BillsPage() {
  usePageTitle('Räkningar');

  const search = useSearch({ from: '/bills/' });
  const navigate = useNavigate();
  const selectedBillId = search.bill ?? null;

  const [formOpen, setFormOpen] = useState(false);
  const [editingBill, setEditingBill] = useState<EditableBill>(null);

  const listQuery = trpc.recurringBill.list.useQuery({}, { retry: false });
  const conflictsQuery = trpc.recurringBill.conflicts.useQuery(
    { billId: selectedBillId ?? '' },
    {
      enabled: Boolean(selectedBillId),
      retry: false,
    },
  );

  const setSelectedBill = (billId: string | null) => {
    void navigate({
      from: '/bills/',
      search: (prev) => ({
        ...prev,
        bill: billId ?? undefined,
      }),
      replace: true,
    });
  };

  const openCreate = () => {
    setEditingBill(null);
    setFormOpen(true);
  };

  const openEdit = (bill: EditableBillInput) => {
    setEditingBill(bill);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingBill(null);
  };

  if (listQuery.error?.data?.code === 'NOT_FOUND' || listQuery.error?.data?.code === 'FORBIDDEN') {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Du tillhör inget hushåll ännu.</p>
        <Button type="button" onClick={() => void navigate({ to: '/household/create' })}>
          Skapa hushåll
        </Button>
      </div>
    );
  }

  if (listQuery.error) {
    return <div className="text-destructive text-sm">{listQuery.error.message}</div>;
  }

  const bills = listQuery.data ?? [];

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Räkningar</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Fasta kostnader som dras varje månad, med prognos för nästa betalning.
          </p>
          <Link
            to="/bills/history"
            className="mt-1 inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-primary hover:underline sm:min-h-0"
          >
            <ChartColumnIncreasing className="size-4" aria-hidden />
            Kostnadshistorik
          </Link>
        </div>
        {bills.length > 0 && (
          <Button type="button" className="shrink-0" onClick={openCreate}>
            Ny räkning
          </Button>
        )}
      </header>

      {listQuery.isLoading ? (
        <CardSkeleton lines={6} />
      ) : bills.length === 0 ? (
        <EmptyState
          icon="🧾"
          title="Inga räkningar"
          description="Skapa en räkning och koppla regler för att börja följa betalningar över tid."
          action={{
            label: 'Skapa räkning',
            onClick: openCreate,
          }}
        />
      ) : (
        <BillsList
          bills={bills}
          selectedBillId={selectedBillId}
          selectedBillConflicts={conflictsQuery.data}
          selectedBillConflictsLoading={conflictsQuery.isLoading}
          selectedBillConflictsError={conflictsQuery.error?.message ?? null}
          onSelectBill={setSelectedBill}
          onCreate={openCreate}
          onEdit={openEdit}
        />
      )}

      <BillForm open={formOpen} bill={editingBill} onClose={closeForm} onSaved={closeForm} />
    </div>
  );
}
